import React, { useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Box,
  HStack,
  Input,
  IconButton,
  Text,
  SelectRoot,
  SelectTrigger,
  SelectContent,
  SelectItem,
  createListCollection,
} from "@chakra-ui/react";
import { FiTrash2, FiUpload } from "react-icons/fi";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
} from "ag-grid-community";

// Register community modules (no enterprise)
ModuleRegistry.registerModules([AllCommunityModule]);

// Base data shape
interface DataRow {
  category: string;
  product: string;
  price: number;
}

// Union type for manual grouping
type RowType = { type: "group"; key: string; count: number } | DataRow;

const Leads: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const rawData: DataRow[] = useMemo(
    () => [
      { category: "Fruit", product: "Apple", price: 1.2 },
      { category: "Fruit", product: "Banana", price: 0.8 },
      { category: "Veg", product: "Carrot", price: 0.6 },
    ],
    []
  );

  const statusOptions = [
    { id: "", label: "All" },
    { id: "New", label: "New" },
    { id: "Contacted", label: "Contacted" },
    { id: "Qualified", label: "Qualified" },
  ];
  const collection = createListCollection({ items: statusOptions });

  // 1) Optionally filter rawData by search and statusFilter
  const filteredData = useMemo(() => {
    return rawData.filter((row) => {
      const matchesSearch = row.product
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "" || (row as any).status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rawData, search, statusFilter]);

  // 2) Group and flatten rows
  const rowData = useMemo<RowType[]>(() => {
    const groups = filteredData.reduce<Record<string, DataRow[]>>(
      (acc, row) => {
        (acc[row.category] ||= []).push(row);
        return acc;
      },
      {}
    );

    const rows: RowType[] = [];
    Object.entries(groups).forEach(([key, items]) => {
      rows.push({ type: "group", key, count: items.length });
      if (expandedGroups.has(key)) {
        rows.push(...items);
      }
    });
    return rows;
  }, [filteredData, expandedGroups]);

  // 3) Define columns with custom renderer
  const columnDefs = useMemo<ColDef<RowType>[]>(
    () => [
      {
        headerName: "Category / Product",
        field: "product",
        cellRenderer: (params: ICellRendererParams<RowType>) => {
          const data = params.data!;
          if ((data as any).type === "group") {
            const { key, count } = data as any;
            const isExpanded = expandedGroups.has(key);
            return (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const next = new Set(expandedGroups);
                  if (isExpanded) next.delete(key);
                  else next.add(key);
                  setExpandedGroups(next);
                }}
              >
                {isExpanded ? "▼" : "▶"}&nbsp;
                <strong>{key}</strong>&nbsp;
                <span style={{ color: "#666" }}>({count})</span>
              </span>
            );
          }
          return <span>{(data as DataRow).product}</span>;
        },
      },
      {
        headerName: "Price",
        field: "price",
        valueFormatter: (params) => {
          const data = params.data!;
          return (data as any).type === "group"
            ? ""
            : `$${(data as DataRow).price.toFixed(2)}`;
        },
      },
    ],
    [expandedGroups]
  );

  return (
    <Box p={6} bg="gray.50" minH="100vh">
      <HStack justify="space-between" mb={4} wrap="wrap" gap={4}>
        <Text fontSize="xl" fontWeight="bold">
          Leads
        </Text>
        <HStack gap={2} flexWrap="wrap">
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxW="200px"
          />
          <SelectRoot
            collection={collection}
            onValueChange={({ value }) =>
              setStatusFilter(Array.isArray(value) ? value[0] : value)
            }
          >
            <SelectTrigger maxW="180px">
              {statusFilter || "Filter by status"}
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.id} item={option}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          <IconButton
            aria-label="Upload"
            variant="outline"
            children={<FiUpload />}
          />
          <IconButton
            aria-label="Delete"
            variant="outline"
            children={<FiTrash2 />}
          />
        </HStack>
      </HStack>

      <Box
        className="ag-theme-alpine"
        style={{ height: "600px", width: "100%" }}
      >
        <AgGridReact<RowType>
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(params) => {
            const d = params.data!;
            return (d as any).type === "group"
              ? `group-${(d as any).key}`
              : `row-${(d as DataRow).category}-${(d as DataRow).product}`;
          }}
          defaultColDef={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
};

export default Leads;
